import React, {useRef, useState} from 'react';

import {refreshAnalysisTemplate} from 'analysis-tool/polling/refreshPipeline';
import {assert} from 'Asserts';
import {createDataFrame} from 'data-frames/network/DataFrameDAO';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as PipelineDAO from 'pipelines/network/PipelineDAO';
import type {CreateStepParams} from 'pipelines/network/PipelineDAO';
import {getInputStepDatas, getOutputStepDatas} from 'pipelines/StepConnectionUtils';
import {withLoadingScreen} from 'util/Promises';
import {JSONValue} from 'util/Types';

import {AnalysisRightSidebarItemType, RightSidebarComponentProps} from '../../RightSidebarTypes';
import ConfigureColumnMappingPhase from './ConfigureColumnMappingPhase';
import ConfigureTemplateInputPhase, {
  ConfigureTemplateInputState,
  DEFAULT_CONFIGURE_TEMPLATE_INPUT_STATE,
  shouldAddApplyColumnMappingStep,
  TemplateInputType,
} from './ConfigureTemplateInputPhase';

/**
 * Build `CreateStepParams` to add a static input table.
 *
 * A static input table is represented by a TEMPLATE_INPUT step.
 *
 * TEMPLATE_INPUT steps are not executable; instead their output data frame is directly patched in.
 */
async function getStaticInputTableStepsToAdd(
  datasetId: string,
  name: string
): Promise<Array<CreateStepParams>> {
  // Copy the data frame which backs the selected dataset, so it can be independently patched into the
  // TEMPLATE_INPUT step.
  const {createdIds} = await createDataFrame({name, datasetId});
  assert(createdIds.length === 1, 'Expected exactly one created data frame');
  const [outputStepData] = getOutputStepDatas(PipelineStepType.TEMPLATE_INPUT);

  return [
    {
      stepType: PipelineStepType.TEMPLATE_INPUT,
      stepVersion: 1,
      config: undefined, // This step does not have a config
      outputStepDatas: [{name: outputStepData.systemName, rawDataIds: [createdIds[0]]}],
      runAfterCreate: false, // The TEMPLATE_INPUT step is not run; its outputs are directly patched in.
    },
  ];
}

/**
 * Build `CreateStepParams` to add a variable input table.
 *
 * A variable input table is represented by a VARIABLE_TEMPLATE_INPUT_TABLE step type.
 *
 * VARIABLE_TEMPLATE_INPUT_TABLE steps behave like a normal step type, except:
 * - VARIABLE_TEMPLATE_INPUT_TABLE steps are synced with `AnalysisTemplateDataFrameVariable` models.
 * - When applying the template, the VARIABLE_TEMPLATE_INPUT_TABLE step will be dropped.
 *
 * If `columnMappingConfig` is provided, an `APPLY_COLUMN_MAPPING` step is added downstream so the template
 * can map columns automatically when applied.
 */
function getVariableInputTableStepsToAdd(
  datasetId: string,
  name: string,
  description: string,
  requireColumnMapping: boolean,
  columnMappingConfig: Record<string, JSONValue> | null
): Array<CreateStepParams> {
  const [outputStepData] = getOutputStepDatas(PipelineStepType.VARIABLE_TEMPLATE_INPUT_TABLE);
  const [inputStepData] = getInputStepDatas(PipelineStepType.APPLY_COLUMN_MAPPING);

  const stepsToAdd: Array<CreateStepParams> = [
    {
      stepType: PipelineStepType.VARIABLE_TEMPLATE_INPUT_TABLE,
      stepVersion: 0,
      config: {datasetId, name, description, requireColumnMapping},
      incomingEdges: [],
      runAfterCreate: true,
    },
  ];

  // End-user column mapping isn't required, so an APPLY_COLUMN_MAPPING step is added to the template instead.
  if (!requireColumnMapping && columnMappingConfig != null) {
    stepsToAdd.push({
      stepType: PipelineStepType.APPLY_COLUMN_MAPPING,
      stepVersion: 0,
      config: columnMappingConfig,
      incomingEdges: [
        {
          fromStepIndex: 0,
          fromStepDataName: outputStepData.systemName,
          fromStepDataIndex: null,
          toStepDataName: inputStepData.systemName,
          toStepDataIndex: 0,
        },
      ],
    });
  }

  return stepsToAdd;
}

enum Phase {
  CONFIGURE_TEMPLATE_INPUT = 'CONFIGURE_TEMPLATE_INPUT',
  CONFIGURE_COLUMN_MAPPING = 'CONFIGURE_COLUMN_MAPPING',
}

/**
 * Configures a variable or static template input step.
 *
 * Depending on whether the user selects "Variable" or "Static" either a VARIABLE_TEMPLATE_INPUT_TABLE or
 * TEMPLATE_INPUT step will be added, respectively.
 *
 * "Variable" input tables can be configured to require end users to map columns before applying the template
 * (`requireColumnMapping`); if the template builder opts out of requiring end-user column mapping, the
 * template builder will be progressed to configure an `APPLY_COLUMN_MAPPING` step within the template itself.
 *
 * The flow has two phases:
 * 1. `ConfigureTemplateInputPhase`: The user picks a dataset, name, and input type.
 * 2. `ConfigureColumnMappingPhase`: Configure a `APPLY_COLUMN_MAPPING`.
 */
export default function AddTemplateInputSourceRightSidebar({
  analysis,
  onChangeSelectedRightSidebarItem,
}: RightSidebarComponentProps<AnalysisRightSidebarItemType.ADD_TEMPLATE_INPUT_SOURCE>): JSX.Element {
  const [phase, setPhase] = useState<Phase>(Phase.CONFIGURE_TEMPLATE_INPUT);
  const [templateInputState, setTemplateInputState] = useState<ConfigureTemplateInputState>(
    DEFAULT_CONFIGURE_TEMPLATE_INPUT_STATE
  );
  const [columnMappingConfig, setColumnMappingConfig] = useState<Record<string, JSONValue> | null>(null);

  // `isSaving` doesn't update synchronously, so a fast double-click could enter `onSave` twice.
  const isSavingRef = useRef(false);

  const onSave = async (): Promise<void> => {
    if (isSavingRef.current) {
      return;
    }
    const {datasetId, name, description, requireColumnMapping, templateInputType} = templateInputState;
    if (datasetId == null) {
      return;
    }
    try {
      isSavingRef.current = true;
      await withLoadingScreen(
        (async () => {
          const stepsToAdd =
            templateInputType === TemplateInputType.VARIABLE
              ? getVariableInputTableStepsToAdd(
                  datasetId,
                  name.trim(),
                  description.trim(),
                  requireColumnMapping,
                  columnMappingConfig
                )
              : await getStaticInputTableStepsToAdd(datasetId, name.trim());
          await PipelineDAO.addAndUpdatePipelineSteps(analysis.id, {stepsToAdd, stepsToUpdate: []});
          await refreshAnalysisTemplate(analysis.id);
          onChangeSelectedRightSidebarItem(null);
        })()
      );
    } finally {
      isSavingRef.current = false;
    }
  };

  const onNext = async (): Promise<void> => {
    if (shouldAddApplyColumnMappingStep(templateInputState)) {
      setPhase(Phase.CONFIGURE_COLUMN_MAPPING);
    } else {
      await onSave();
    }
  };

  if (phase === Phase.CONFIGURE_TEMPLATE_INPUT || templateInputState.datasetId == null) {
    return (
      <ConfigureTemplateInputPhase
        state={templateInputState}
        setState={(nextState) => {
          setColumnMappingConfig(null); // Clear column mapping, in case the user moved back and made an edit.
          setTemplateInputState(nextState);
        }}
        onCancel={() => {
          onChangeSelectedRightSidebarItem({type: AnalysisRightSidebarItemType.ADD_SOURCE_STEP});
        }}
        onNext={onNext}
        nextButtonLabel={shouldAddApplyColumnMappingStep(templateInputState) ? 'Next' : 'Save'}
      />
    );
  } else {
    return (
      <ConfigureColumnMappingPhase
        key={templateInputState.datasetId}
        datasetId={templateInputState.datasetId}
        config={columnMappingConfig}
        setConfig={setColumnMappingConfig}
        onBack={() => {
          setPhase(Phase.CONFIGURE_TEMPLATE_INPUT);
        }}
        onSave={onSave}
      />
    );
  }
}
